// src/components/SystemRecommendation/components/ExecutiveSummary.tsx

import React from 'react';
import { ViewMode, PerformanceRange } from '../types';

interface ExecutiveSummaryProps {
  recommendedSystem: string;
  whyThisWins?: string[];
  expectedGasUse?: PerformanceRange;
  expectedElectricityUse?: PerformanceRange;
  standingLoss?: PerformanceRange;
  scop?: PerformanceRange;
  confidence: 'High' | 'Medium' | 'Low';
  confidenceInputs?: {
    confirmed: number;
    inferred: number;
    unknown: number;
    details: string[];
  };
  heatPumpReady?: boolean;
  viewMode: ViewMode;
}

/**
 * Format a performance range for display
 */
function formatRange(range?: PerformanceRange): string | undefined {
  if (!range) return undefined;
  
  if (range.min === range.max) {
    return `${range.min}${range.unit}`;
  }
  
  if (range.unit === '%' || !range.unit) {
    return `${range.min}–${range.max}${range.unit || ''}`;
  }
  
  return `~${range.min}–${range.max} ${range.unit}`;
}

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({
  recommendedSystem,
  whyThisWins,
  expectedGasUse,
  expectedElectricityUse,
  standingLoss,
  scop,
  confidence,
  confidenceInputs,
  heatPumpReady,
  viewMode
}) => {
  if (viewMode === 'customer') {
    return (
      <div className="executive-summary customer">
        <h2 className="summary-title">At a Glance</h2>
        <div className="summary-content">
          <div className="summary-item">
            <span className="summary-label">Recommended:</span>
            <span className="summary-value">{recommendedSystem}</span>
          </div>
          {whyThisWins && whyThisWins.length > 0 && (
            <div className="summary-item why-wins">
              <span className="summary-label">Why this works:</span>
              <ul className="why-wins-list">
                {whyThisWins.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
          {expectedGasUse && (
            <div className="summary-item">
              <span className="summary-label">Expected gas use:</span>
              <span className="summary-value">{formatRange(expectedGasUse)}/year</span>
            </div>
          )}
          {expectedElectricityUse && (
            <div className="summary-item">
              <span className="summary-label">Expected electricity use:</span>
              <span className="summary-value">{formatRange(expectedElectricityUse)}/year</span>
            </div>
          )}
          {scop && (
            <div className="summary-item">
              <span className="summary-label">Expected SCOP:</span>
              <span className="summary-value">{formatRange(scop)}*</span>
              {scop.footnote && (
                <span className="summary-footnote">*{scop.footnote}</span>
              )}
            </div>
          )}
          {standingLoss && (
            <div className="summary-item">
              <span className="summary-label">Cylinder loss:</span>
              <span className="summary-value">{formatRange(standingLoss)}</span>
            </div>
          )}
          <div className="summary-item">
            <span className="summary-label">Confidence:</span>
            <span className={`summary-value confidence-${confidence.toLowerCase()}`}>
              {confidence}
              {confidence !== 'High' && ' (will confirm during survey)'}
            </span>
          </div>
          {heatPumpReady && (
            <div className="summary-item">
              <span className="summary-label">Future upgrade:</span>
              <span className="summary-value">✓ Heat pump ready</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Technical mode - Enhanced 5-line executive summary
  const formattedGasUse = formatRange(expectedGasUse);
  const formattedElecUse = formatRange(expectedElectricityUse);
  const formattedLoss = formatRange(standingLoss);
  const formattedScop = formatRange(scop);
  
  return (
    <div className="executive-summary technical">
      <div className="summary-card">
        <h2 className="summary-title">RECOMMENDED SYSTEM</h2>
        <div className="summary-system-name">{recommendedSystem}</div>
        
        {whyThisWins && whyThisWins.length > 0 && (
          <div className="summary-section">
            <h3 className="section-label">Why this wins:</h3>
            <ul className="why-wins-list">
              {whyThisWins.map((reason, idx) => (
                <li key={idx}>• {reason}</li>
              ))}
            </ul>
          </div>
        )}
        
        {(formattedGasUse || formattedElecUse || formattedScop) && (
          <div className="summary-section">
            <h3 className="section-label">
              {formattedScop ? 'Estimated Performance:' : 'Estimated Annual Energy Use:'}
            </h3>
            {formattedGasUse && (
              <div className="summary-row">
                <span className="summary-metric">Gas: {formattedGasUse}/year</span>
              </div>
            )}
            {formattedElecUse && (
              <div className="summary-row">
                <span className="summary-metric">Electricity: {formattedElecUse}/year</span>
              </div>
            )}
            {formattedScop && (
              <div className="summary-row">
                <span className="summary-metric">SCOP: {formattedScop}*</span>
                {scop?.footnote && (
                  <div className="summary-footnote">*{scop.footnote}</div>
                )}
              </div>
            )}
          </div>
        )}
        
        {formattedLoss && (
          <div className="summary-section">
            <h3 className="section-label">Standing Loss:</h3>
            <div className="summary-row">
              <span className="summary-metric">{formattedLoss} (usage dependent)</span>
            </div>
            {standingLoss?.footnote && (
              <div className="summary-footnote">{standingLoss.footnote}</div>
            )}
          </div>
        )}
        
        <div className="summary-section">
          <h3 className="section-label">Confidence:</h3>
          <div className="confidence-display">
            <span className={`confidence-badge confidence-${confidence.toLowerCase()}`}>
              {confidence}
            </span>
            {confidenceInputs && (
              <div className="confidence-breakdown">
                <div className="breakdown-text">
                  {confidenceInputs.confirmed} confirmed • {confidenceInputs.inferred} inferred • {confidenceInputs.unknown} unknown
                </div>
                {confidence !== 'High' && (
                  <div className="confidence-note">
                    {confidence === 'Medium' 
                      ? 'Heat loss inferred, radiator sizing unconfirmed' 
                      : 'Multiple key assumptions required'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {heatPumpReady && (
          <div className="summary-section upgrade-path">
            <h3 className="section-label">Upgrade Path:</h3>
            <div className="summary-row">
              <span className="summary-metric">✓ Heat pump upgrade pathway retained</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveSummary;
